import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = import.meta.env.VITE_API_URL;
const API_URL = `${BASE_URL}/api/repair`;

const token = () => localStorage.getItem("jwt");

export interface RepairDeleted {
  _id: string;
  orderNumber: string;
  requestType: "repair" | "replacement";
  companyName: string;
  createdBy?: { username: string; employeeId?: string };
  receivingDate?: string;
  expectedDeliveryDate?: string;
  issueDescription?: string;
  remark?: string;
  serialId?: string;
  isDeleted?: boolean;
  deletedAt?: string;
}

interface RecycleState {
  items: RepairDeleted[];
  total: number;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  message: string | null;
}

const initialState: RecycleState = {
  items: [],
  total: 0,
  status: "idle",
  error: null,
  message: null,
};

export const fetchDeletedRepairsAsync = createAsyncThunk(
  "recycleBin/fetchDeleted",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const authToken = token(); // ✅ single source of truth

      if (!authToken) {
        throw new Error("Auth token missing");
      }

      const res = await axios.get(`${API_URL}/deleted`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        params,
      });

      return res.data;
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Unauthorized - Token invalid"
      );
    }
  }
);



export const restoreRepairAsync = createAsyncThunk(
  "recycleBin/restore",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${API_URL}/restore/${id}`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const permanentDeleteRepairAsync = createAsyncThunk(
  "recycleBin/permanentDelete",
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`${API_URL}/permanent/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const recycleSlice = createSlice({
  name: "recycleBin",
  initialState,
  reducers: {
    clearMessages(state) {
      state.message = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeletedRepairsAsync.pending, (state) => { state.status = "loading"; })
      .addCase(fetchDeletedRepairsAsync.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload.data || [];
        state.total = action.payload.total || state.items.length;
      })
      .addCase(fetchDeletedRepairsAsync.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) || "Failed to fetch deleted repairs";
      })


      .addCase(restoreRepairAsync.fulfilled, (state, action) => {
        state.message = "Restored";
        const updated = action.payload?.data || action.payload;
        if (updated?._id) {
          state.items = state.items.filter(i => i._id !== updated._id);
          state.total = Math.max(0, state.total - 1);
        }
      })
      .addCase(restoreRepairAsync.rejected, (state, action) => {
        state.error = action.payload as string || "Failed to restore";
      })

      .addCase(permanentDeleteRepairAsync.fulfilled, (state) => {
        state.message = "Permanently deleted";
      })
      .addCase(permanentDeleteRepairAsync.rejected, (state, action) => {
        state.error = action.payload as string || "Failed to permanently delete";
      });
  },
});

export const { clearMessages } = recycleSlice.actions;
export default recycleSlice.reducer;
